import { z } from 'zod';
import { UTCDate } from '@date-fns/utc';
import { isEmpty } from 'lodash';
import { format } from 'date-fns';

export const dateToUTC = z.union([z.string(), z.date()]).transform(date => new UTCDate(date));

export const coerceStringToNumberOrNull = z
  .string()
  .transform(value => (isEmpty(value) || isNaN(Number(value)) ? null : parseFloat(value)));

export const dateToUTCOrNull = z
  .union([z.string(), z.date()])
  .transform(date => (date === '' ? null : new UTCDate(date)));

export const dateToYearMonthDay = z.coerce.date().transform(d => format(d, 'yyyy-MM-dd'));

export const dateToEpoch = z.coerce.date().transform(d => d.getTime());

export const MoneyNumber = z.union([
  z.number(),
  z.string().transform((s, ctx) => {
    const t = s.trim();
    if (!/\d/.test(t)) {
      ctx.addIssue({ code: 'custom', message: 'Not a valid number.' });
      return z.NEVER;
    }
    // Normalize: parentheses negative, strip commas/currency/symbols (US style)
    const core = t
      .replace(/^\((.*)\)$/, '-$1') // "(123.45)" -> "-123.45"
      .replace(/,/g, '') // "10,000.00" -> "10000.00"
      .replace(/[^0-9.+-]/g, ''); // drop $, spaces, letters, etc.

    // Validate a plain decimal number (no EU decimal comma)
    if (!/^[+-]?\d+(\.\d+)?$/.test(core)) {
      ctx.addIssue({ code: 'custom', message: 'Not a valid number.' });
      return z.NEVER;
    }

    return Number(core);
  })
]);

export function getRequiredFields<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>
): { [K in keyof T]: boolean } {
  const shape = schema.shape;
  const requiredFields = {} as { [K in keyof T]: boolean };

  (Object.entries(shape) as [keyof T, z.ZodTypeAny][]).forEach(([key, value]) => {
    requiredFields[key] = !value.isOptional();
  });

  return requiredFields;
}

export type InOf<S extends z.ZodTypeAny> = z.input<S>;
export type OutOf<S extends z.ZodTypeAny> = z.output<S>;
